/** @typedef {{ version: number; decidedAt: string; essential: true; functional: boolean; analytics: boolean; doNotSellOrShare: boolean }} CookieConsent */

export const CONSENT_VERSION = 1;
export const CONSENT_STORAGE_KEY = "wv_cookie_consent";

export const COOKIE_CATEGORIES = {
  essential: {
    id: "essential",
    label: "Strictly necessary",
    description:
      "Required for security, authentication, load balancing, and core site functions. These cannot be turned off.",
    required: true,
  },
  functional: {
    id: "functional",
    label: "Functional",
    description:
      "Remember preferences such as theme, live support chat session IDs, and similar features that improve your experience.",
    required: false,
  },
  analytics: {
    id: "analytics",
    label: "Analytics",
    description:
      "Help us understand how the site is used so we can improve performance and content. We do not use these for cross-site advertising.",
    required: false,
  },
};

/** @returns {CookieConsent | null} */
export function readConsent() {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return {
      version: CONSENT_VERSION,
      decidedAt: parsed.decidedAt || new Date().toISOString(),
      essential: true,
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      doNotSellOrShare: Boolean(parsed.doNotSellOrShare),
    };
  } catch {
    return null;
  }
}

/** @param {Partial<CookieConsent> & { functional?: boolean; analytics?: boolean; doNotSellOrShare?: boolean }} prefs */
export function saveConsent(prefs) {
  /** @type {CookieConsent} */
  const next = {
    version: CONSENT_VERSION,
    decidedAt: new Date().toISOString(),
    essential: true,
    functional: Boolean(prefs.functional),
    analytics: Boolean(prefs.analytics),
    doNotSellOrShare: Boolean(prefs.doNotSellOrShare),
  };
  localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("wv:cookie-consent", { detail: next }));
  }
  if (!next.functional) {
    sessionStorage.removeItem("wv_chat_session");
    sessionStorage.removeItem("wv_chat_session_started");
    localStorage.removeItem("wv_chat_session");
    localStorage.removeItem("wv_chat_session_started");
    localStorage.removeItem("wv_chat_email");
    localStorage.removeItem("theme");
  }
  return next;
}

export function hasConsentDecision() {
  return readConsent() !== null;
}

export function allowsCategory(categoryId) {
  if (categoryId === "essential") return true;
  const consent = readConsent();
  if (!consent) return false;
  if (categoryId === "functional") return consent.functional;
  if (categoryId === "analytics") return consent.analytics;
  return false;
}

export function acceptAllConsent() {
  return saveConsent({ functional: true, analytics: true, doNotSellOrShare: false });
}

export function rejectNonEssentialConsent() {
  return saveConsent({ functional: false, analytics: false, doNotSellOrShare: true });
}

export function setDoNotSellOrShare(enabled) {
  const current = readConsent() || {
    functional: false,
    analytics: false,
    doNotSellOrShare: false,
  };
  return saveConsent({ ...current, doNotSellOrShare: enabled });
}
